// status-count-by-period-chart.tsx — Ticket open/closed count over time with period toggle.
import { useState } from 'react';
import { Chart, CHART_COLORS } from '@vnetwork/ui/charts';
import { Card, CardBody, CardTitle, Skeleton, SegmentedControl } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useTicketStatusCountByPeriod, type StatDateParams, type StatPeriodParams } from '@/api/tickets-statistics';

const SERIES = [
  { key: 'open_tickets',   label: 'Open',   color: CHART_COLORS[1] }, // orange
  { key: 'closed_tickets', label: 'Closed', color: CHART_COLORS[2] }, // green
];

type Period = 'day' | 'week' | 'month';

const PERIOD_ITEMS: { value: Period; label: string }[] = [
  { value: 'day',   label: 'Day'   },
  { value: 'week',  label: 'Week'  },
  { value: 'month', label: 'Month' },
];

interface Props {
  dateParams: StatDateParams;
}

export function StatusCountByPeriodChart({ dateParams }: Props) {
  const [period, setPeriod] = useState<Period>('month');

  const params: StatPeriodParams = { ...dateParams, period };
  const { data, isLoading, isError } = useTicketStatusCountByPeriod(params);

  const rows = (data?.results ?? []).map((d) => ({
    label: d.period,
    open_tickets:   d.open_tickets,
    closed_tickets: d.closed_tickets,
  }));

  return (
    <Card flat>
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--vn-space-3)' }}>
          <CardTitle>Ticket Volume by Period</CardTitle>
          <SegmentedControl
            size="sm"
            aria-label="Period"
            value={period}
            onValueChange={(v) => setPeriod(v as Period)}
            items={PERIOD_ITEMS}
          />
        </div>
        {isError ? (
          <Alert tone="danger" title="Failed to load period data" />
        ) : isLoading ? (
          <Skeleton style={{ height: 260 }} />
        ) : rows.length === 0 ? (
          <EmptyState title="No data" description="No ticket volume data in this date range." />
        ) : (
          <Chart
            type="line"
            data={rows}
            xKey="label"
            series={SERIES}
            height={260}
            showGrid
            showXAxis
            showYAxis
            curve="monotone"
            valueFormatter={(v) => String(v ?? 0)}
          />
        )}
      </CardBody>
    </Card>
  );
}
